import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { TargetEntityType } from "@/shared/domain/customFields";

import { customFieldQueryKeys } from "./customFieldQueryKeys";

interface DeleteCustomFieldArgs {
  fieldId: string;
  // Owning org + entity type, passed in so the success handler can scope cache
  // invalidation — the DELETE response has no body to read them from.
  orgId: string;
  entityType: TargetEntityType;
}

/**
 * Deletes a custom-field definition (by `field_id`) in the current org. On
 * success it invalidates the cached lists for that field's entity type;
 * `useCustomFields` then polls the row Deleting -> gone, or surfaces a Failed
 * status if the async teardown fails.
 */
export function useDeleteCustomField() {
  const { customFieldService } = useDomainServices();
  const queryClient = useQueryClient();
  const orgId = useCurrentOrgId();

  return useMutation<void, Error, DeleteCustomFieldArgs>({
    mutationFn: ({ fieldId }) =>
      customFieldService.deleteCustomField(fieldId, { orgId }),
    onSuccess: (_data, { orgId: fieldOrgId, entityType }) => {
      void queryClient.invalidateQueries({
        queryKey: customFieldQueryKeys.listsForEntity(fieldOrgId, entityType),
      });
    },
  });
}
